import { ListTodo } from 'lucide-react'
import type { Status } from '@/types/enums'

/**
 * Props for the NodeProgress component
 */
interface NodeProgressProps<T extends { status: Status | string }> {
  /**
   * Array of items with status property to calculate completion from
   */
  items: T[]
  /**
   * Optional label to display (defaults to "Completion:")
   */
  label?: string
  /**
   * Icon to display (defaults to ListTodo)
   */
  icon?: React.ComponentType<{ className?: string }>
  /**
   * Additional CSS classes for the container
   */
  className?: string
  /**
   * Custom completion status value to check against (defaults to 'Done')
   */
  completedStatus?: Status | string
}

/**
 * A reusable component for displaying completion percentage based on node status
 *
 * @example
 * ```tsx
 * // Basic usage with tasks
 * <NodeProgress items={project.tasks} />
 *
 * // With custom label and icon
 * <NodeProgress
 *   items={project.tasks}
 *   label="Progress:"
 *   icon={CheckCircle2}
 * />
 *
 * // With custom completion status
 * <NodeProgress
 *   items={items}
 *   completedStatus="Completed"
 * />
 * ```
 */
export function NodeProgress<T extends { status: Status | string }>({
  items,
  label = 'Completion:',
  icon: Icon = ListTodo,
  className = '',
  completedStatus = 'Done',
}: NodeProgressProps<T>) {
  const completedItems = items.filter((item) => item.status === completedStatus).length
  const totalItems = items.length
  const completionPercentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <Icon className="h-4 w-4 mr-1" />
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground">{completionPercentage}%</span>
    </div>
  )
}
